'use client';

import { FC, useEffect, useRef } from 'react';
import { Content } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

/**
 * Props for `FeatureMetrics`.
 */
export type FeatureMetricsProps =
  SliceComponentProps<Content.FeatureMetricsSlice>;

/**
 * Component for "FeatureMetrics" Slices.
 * Displays key metrics/stats in a three-column layout with icons.
 */
const FeatureMetrics: FC<FeatureMetricsProps> = ({ slice }) => {
  const { headline, metrics } = slice.primary;

  const headlineRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLDivElement | null)[] = [];

    if (headlineRef.current) {
      const element = headlineRef.current;
      currentElements.push(element);
      element.classList.add(
        'opacity-0',
        'translate-y-12',
        'transition-all',
        'duration-700',
        'ease-out',
      );
      element.style.transitionDelay = '0ms';
      observer.observe(element);
    }

    metricsRef.current.forEach((metricEl, index) => {
      if (metricEl) {
        currentElements.push(metricEl);
        metricEl.classList.add(
          'opacity-0',
          'translate-y-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        metricEl.style.transitionDelay = `${(index + 1) * 150}ms`;
        observer.observe(metricEl);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [metrics]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-[150px]"
      style={{
        backgroundColor: '#0e1010',
      }}
    >
      <div className="max-w-[1140px] mx-auto">
        <div className="flex flex-col gap-6 items-center">
          {/* Headline */}
          {headline && (
            <div
              ref={headlineRef}
              className="w-full max-w-[1040px] text-center"
            >
              <PrismicRichText
                field={headline}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-[42px] font-medium text-white leading-[1.2]">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}

          {/* Metrics Grid */}
          {metrics && metrics.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              {metrics.map((metric, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    metricsRef.current[index] = el;
                  }}
                  className="flex flex-col items-center justify-center px-6 py-4 rounded-3xl backdrop-blur-[10px]"
                  style={{
                    borderRadius: '24px',
                    background:
                      'linear-gradient(180deg, #002726 0%, rgba(0, 0, 0, 0) 100%)',
                    boxShadow: '0px 4.325px 64.871px 0px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <div className="flex flex-col gap-2 items-start justify-center w-full">
                    {/* Icon */}
                    {metric.icon && (
                      <div className="w-12 h-12">
                        <PrismicNextImage
                          field={metric.icon}
                          className="w-full h-full object-contain"
                          fallbackAlt=""
                        />
                      </div>
                    )}

                    {/* Value/Stat */}
                    {metric.value && (
                      <p className="text-3xl lg:text-[40px] font-bold text-white leading-[1.2]">
                        {metric.value}
                      </p>
                    )}

                    {/* Description */}
                    {metric.description && (
                      <div className="text-sm font-medium text-white leading-[1.5]">
                        <PrismicRichText
                          field={metric.description}
                          components={{
                            paragraph: ({ children }) => <p>{children}</p>,
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureMetrics;
